import axios from "axios";

// Get API base URL from environment or default
const API_BASE_URL = process.env.REACT_APP_API_URL;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000,
  withCredentials: true,
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add timestamp to prevent caching issues
    config.params = {
      ...config.params,
      _t: Date.now(),
    };
    return config;
  },
  (error) => {
    console.error("Request error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Return the data directly if successful
    return response.data.success !== false
      ? response.data
      : Promise.reject(response.data);
  },
  (error) => {
    console.error("Response error:", error);

    // Handle different error cases
    if (error.response) {
      // Server responded with error status
      const message = error.response.data?.message || "An error occurred";
      return Promise.reject({
        message,
        status: error.response.status,
        data: error.response.data,
      });
    } else if (error.request) {
      // Request was made but no response received
      return Promise.reject({
        message: "Network error. Please check your connection.",
        status: 0,
      });
    } else {
      // Something else happened
      return Promise.reject({
        message: error.message || "An unexpected error occurred",
        status: 0,
      });
    }
  }
);

// Profile API
export const profileAPI = {
  getProfile: () => api.get("/profile"),
};

// Projects API
export const projectsAPI = {
  getProjects: (params = {}) => api.get("/projects", { params }),
  getProject: (id) => api.get(`/projects/${id}`),
  getFeaturedProjects: () =>
    api.get("/projects", { params: { featured: true, limit: 6 } }),
};

// Skills API
export const skillsAPI = {
  getSkills: (params = {}) => api.get("/skills", { params }),
  getSkillsByCategory: (category) =>
    api.get("/skills", { params: { category } }),
  getSkillsStats: () => api.get("/skills/categories/stats"),
};

// Contact API
export const contactAPI = {
  sendMessage: (data) => api.post("/contact", data),
};

// Utility functions
export const handleApiError = (
  error,
  defaultMessage = "Something went wrong"
) => {
  if (error?.message) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  return defaultMessage;
};

// Image URL helper
export const getImageUrl = (imagePath) => {
  if (!imagePath) return null;

  // If it's already a full URL (Cloudinary), return as-is
  if (imagePath.startsWith("http")) {
    return imagePath;
  }

  // If it's a local path, prepend the API base URL
  const baseUrl = process.env.REACT_APP_API_URL;
  return `${baseUrl}${imagePath}`;
};

// Health check
export const healthCheck = () => api.get("/health");

export default api;
